import { useContext, useEffect, useMemo, useState } from 'react';
import { matchPath } from 'react-router-dom';
import { isNull } from 'lodash-es';
// eslint-disable-next-line @alf/loop-deps
import { useStatuses } from '@namespace/account/src/hooks/useStatuses';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import {
  getLocalStorageValue,
  getSessionStorageValue,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  setLocalStorageValue
} from '@namespace/helpers';
import { useLocation } from '@namespace/i18n';
// eslint-disable-next-line @alf/loop-deps
import {
  NotificationContext,
  removeNotificationAction
} from '@namespace/notifications';
import { NOTIFICATION_TYPES } from '@namespace/notifications/src/constants';
import useNotifications from '@namespace/notifications/src/hooks/useNotifications';
import { useSearchParams } from '@namespace/router';
import { isHigher } from './isHigher';
import { isNotificationRequired } from './isNotificationRequired';
import { isSectionChangedSinceMount } from './isSectionChangedSinceMount';

export const NOTIFICATION_STATUS = {
  OPENED: 'opened',
  CLOSED: 'closed',
  UNINITIALIZED: 'unitialized'
};

const useMandatoryDepositLimit = () => {
  const [isNotificationBeenShown, setIsNotificationBeenShown] = useState(false);
  const [mountedOnSection, setMountedOnSection] = useState(null);
  const [isClosedOnCurrentInstance, setIsClosedOnCurrentInstance] =
    useState(false);
  const {
    isNeedSetupDepositLimitCheck = false,
    isVerificationBeforeLimitFlow = false,
    isMandatoryPopupClosingOnPageChange = true,
    notifications = {}
  } = useOtherSettings();

  const { notificationsTypesByPriorities = [] } = notifications;
  const {
    isNeedSetupLimits,
    isPassportVerifiedByRisk,
    isAdditionalInformationRequired,
    isPassportInVerification
  } = useStatuses();

  const { pathname } = useLocation();
  const { mandatoryLimit, gamingActivity, login, mandatoryLimitGame } =
    useGeneralRoutes();
  const { customNotification } = useNotifications();
  const [{ notificationDispatch, notificationType, priority }] =
    useContext(NotificationContext);

  const { popup } = useSearchParams();

  const pagesRequiringNotificationHiding = useMemo(
    () => [mandatoryLimit, gamingActivity, login].filter((item) => item),
    [gamingActivity, mandatoryLimit, login]
  );

  const pagesRequiringNotificationClosure = useMemo(
    () => [mandatoryLimitGame].filter((item) => item),
    [mandatoryLimitGame]
  );

  const shouldShowNotification = useMemo(() => {
    if (!(isNeedSetupDepositLimitCheck || isVerificationBeforeLimitFlow)) {
      return false;
    }

    const isVerificationRequired = isHigher(
      notificationsTypesByPriorities,
      'UNVERIFIED_USER',
      'MANDATORY_DEPOSIT_LIMIT'
    );

    return isNotificationRequired({
      isVerificationRequired,
      isNeedSetupLimits,
      isPassportVerifiedByRisk,
      isAdditionalInformationRequired,
      isPassportInVerification
    });
  }, [
    isAdditionalInformationRequired,
    isNeedSetupDepositLimitCheck,
    isNeedSetupLimits,
    isPassportInVerification,
    isPassportVerifiedByRisk,
    notificationsTypesByPriorities
  ]);

  useEffect(() => {
    const isSkipCheck =
      !isNeedSetupDepositLimitCheck &&
      (!isVerificationBeforeLimitFlow || isNotificationBeenShown);

    const isVerificationRequired =
      isVerificationBeforeLimitFlow && !isPassportVerifiedByRisk;

    if (
      (isSkipCheck || isVerificationRequired) &&
      !isMandatoryPopupClosingOnPageChange
    ) {
      return;
    }

    const { status, prevSection } = getLocalStorageValue(
      LOCAL_STORAGE_KEYS.NOTIFICATION_MANDATORY_DEPOSIT_LIMIT_STATE,
      {
        status: NOTIFICATION_STATUS.UNINITIALIZED
      }
    );

    const isMountedOnCurrentInstanse = !isNull(mountedOnSection);

    if (
      ((status === NOTIFICATION_STATUS.CLOSED && !isMountedOnCurrentInstanse) ||
        isClosedOnCurrentInstance) &&
      isMandatoryPopupClosingOnPageChange
    ) {
      notificationDispatch(
        removeNotificationAction({
          notificationType: NOTIFICATION_TYPES.MANDATORY_DEPOSIT_LIMIT
        })
      );
      return;
    }

    const prevPathname = getSessionStorageValue(
      SESSION_STORAGE_KEYS.PREV_PATHNAME,
      ''
    );

    const isNotificationHiddenOnPrevPage =
      pagesRequiringNotificationHiding.some((i) =>
        matchPath({ path: i }, prevPathname)
      );

    const shouldOpenNotification = !pagesRequiringNotificationHiding.some((i) =>
      matchPath({ path: i }, pathname)
    );

    const currentSection = pathname.split('/')[1];

    const isNewAppInstance =
      status === NOTIFICATION_STATUS.OPENED && isNull(mountedOnSection);

    const isSectionChanged = isSectionChangedSinceMount({
      currentSection,
      mountedOnSection: isNewAppInstance ? prevSection : mountedOnSection,
      isNotificationHiddenOnPrevPage
    });
    const isUserOnNotificationClosurePage =
      pagesRequiringNotificationClosure.some((page) =>
        matchPath({ path: page }, pathname)
      );
    const isUserOnShowGamePage = pathname.includes('/show-game/');

    if (
      !shouldShowNotification ||
      (isMandatoryPopupClosingOnPageChange &&
        (isSectionChanged ||
          isUserOnNotificationClosurePage ||
          isUserOnShowGamePage))
    ) {
      if (notificationType === NOTIFICATION_TYPES.MANDATORY_DEPOSIT_LIMIT) {
        notificationDispatch(removeNotificationAction({ notificationType }));
      }

      if (
        isNewAppInstance ||
        notificationType === NOTIFICATION_TYPES.MANDATORY_DEPOSIT_LIMIT
      ) {
        setLocalStorageValue(
          LOCAL_STORAGE_KEYS.NOTIFICATION_MANDATORY_DEPOSIT_LIMIT_STATE,
          {
            status: NOTIFICATION_STATUS.CLOSED
          }
        );

        if (!isNewAppInstance) {
          setIsClosedOnCurrentInstance(true);
        }
      }
      return;
    }

    if (
      status === NOTIFICATION_STATUS.OPENED &&
      !isNotificationHiddenOnPrevPage &&
      !isNewAppInstance
    ) {
      return;
    }

    customNotification({
      notificationType: NOTIFICATION_TYPES.MANDATORY_DEPOSIT_LIMIT,
      timeout: null,
      isShown: popup ? false : shouldOpenNotification,
      ...(isNeedSetupDepositLimitCheck ? { priority: 2 } : {})
    });

    setMountedOnSection(currentSection);
    setLocalStorageValue(
      LOCAL_STORAGE_KEYS.NOTIFICATION_MANDATORY_DEPOSIT_LIMIT_STATE,
      {
        status: NOTIFICATION_STATUS.OPENED,
        prevSection: currentSection
      }
    );
    setIsNotificationBeenShown(true);
  }, [
    customNotification,
    isNeedSetupDepositLimitCheck,
    mountedOnSection,
    notificationDispatch,
    notificationType,
    pathname,
    popup,
    priority,
    shouldShowNotification,
    pagesRequiringNotificationHiding,
    pagesRequiringNotificationClosure,
    isVerificationBeforeLimitFlow,
    isMandatoryPopupClosingOnPageChange
  ]);
};

export default useMandatoryDepositLimit;
